import { Button } from '@bizora/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, AlertCircle } from 'lucide-react'
import { authenticateTenantAdmin, authenticatePlatformAdmin, getTenantAdminUrl } from '@bizora/sdk'

// Get platform admin URL - use port 3002 in development, or /platform-admin in production
function getPlatformAdminUrl(path: string = ''): string {
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  
  if (isLocalhost) {
    // Development: use port 3002
    const port = import.meta.env.VITE_PLATFORM_ADMIN_PORT 
      ? parseInt(import.meta.env.VITE_PLATFORM_ADMIN_PORT, 10) 
      : 3002
    return `${protocol}//${hostname}:${port}${path}`
  }
  
  // Production: use folder-based path
  return `/platform-admin${path}`
}

export function Login() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Try tenant authentication first (supports email OR username)
      const tenant = await authenticateTenantAdmin(formData.email, formData.password)

      if (tenant) {
        // Check if tenant is active
        if (tenant.status !== 'active') {
          setError('Your tenant account is not active. Please contact support.')
          setIsLoading(false)
          return
        }

        // Store tenant authentication in localStorage so tenant-admin app can read it
        // This matches the format expected by tenant-admin's UserContext
        localStorage.setItem('currentUser', JSON.stringify({
          id: tenant.id,
          name: tenant.name,
          email: tenant.email || '',
          role: 'owner', // Tenant admin is always owner role
          tenantId: tenant.id,
          tenantSlug: tenant.slug
        }))

        // Redirect to tenant admin portal
        // Use SDK function to get proper URL, then append dashboard path
        const baseAdminUrl = getTenantAdminUrl(tenant.slug) // Returns: /t/{slug}/admin/
        const adminUrl = `${baseAdminUrl}dashboard` // Results in: /t/{slug}/admin/dashboard
        window.location.href = adminUrl
        return
      }

      // If not a tenant, try platform admin authentication
      const platformAdmin = await authenticatePlatformAdmin(formData.email, formData.password)

      if (platformAdmin) {
        // Store platform admin auth
        localStorage.setItem('platformAdminAuth', JSON.stringify({
          id: platformAdmin.id,
          email: platformAdmin.email,
          name: platformAdmin.name,
          role: platformAdmin.role,
          authenticated: true
        }))

        // Redirect to platform admin portal
        const platformUrl = getPlatformAdminUrl('/dashboard')
        window.location.href = platformUrl
        return
      }

      // If neither worked, show error
      setError('Invalid email or password. Please check your credentials.')
      setIsLoading(false)
    } catch (err: any) {
      console.error('Login error:', err)
      setError(err?.message || 'An error occurred during login. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-neutral-900 mb-4">Login</h1>
          <p className="text-xl text-neutral-600">
            Sign in to your account
          </p>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                Email or Username
              </label>
              <input
                type="text"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-lg border border-neutral-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="your@email.com or admin_yourtenant"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Enter your email address or admin username
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-neutral-300 text-primary-600 focus:ring-primary-600"
                />
                <span className="ml-2 text-sm text-neutral-600">Remember me</span>
              </label>
              <a href="#" className="text-sm text-primary-600 hover:underline">
                Forgot password?
              </a>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            <p className="text-sm text-neutral-600 text-center">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-600 font-medium hover:underline">
                Sign up
              </Link>
            </p>

            <div className="pt-4 border-t border-neutral-200">
              <p className="text-xs text-neutral-500 text-center mb-2">
                Platform Administrator?
              </p>
              <a 
                href={getPlatformAdminUrl('/login')} 
                className="block text-center text-sm text-primary-600 font-medium hover:underline"
              >
                Access Platform Admin Portal
              </a>
            </div>
          </form>
        </div>
      </div>
  )
}

